<template>
  <div>
    <!-- Status Change Dialog -->
    <v-dialog v-model="statusDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ selectedUser.AccountDisabled ? 'Enable User' : 'Disable User' }}
        </v-card-title>
        <v-card-text>
          <p v-if="!selectedUser.AccountDisabled">
            Are you sure you want to disable {{ selectedUser.name }}?
          </p>
          <p v-else>
            Are you sure you want to enable {{ selectedUser.name }}?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="statusDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="toggleUserStatus">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete User Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">Delete User</v-card-title>
        <v-card-text>
          <p>Are you sure you want to permanently delete {{ selectedUser.name }}?</p>
          <p class="text-error">This action cannot be undone!</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="deleteUser">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reset Password Dialog -->
    <v-dialog v-model="resetPasswordDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">Send Password Reset</v-card-title>
        <v-card-text>
          <p>Send password reset email to {{ selectedUser.email }}?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="resetPasswordDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="sendPasswordReset">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container>
      <v-row>
        <v-col cols="12" sm="12" md="12" class="d-flex">
          <div class="button-container">
            <button class="add-button" @click="goToPage('/adduser')">Add/Edit User</button>
          </div>
        </v-col>
        
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search Users"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
        
        <v-col cols="12" md="3">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Filter by status"
            @change="getAdminUsers"
          ></v-select>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="adminUsers"
        :search="search"
        :loading="loading"
        loading-text="Loading users..."
      >
        <template v-slot:item.status="{ item }">
          <v-chip :color="item.AccountDisabled ? 'red' : 'green'" dark>
            {{ item.AccountDisabled ? 'Disabled' : 'Active' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn 
                icon 
                v-on="on"
                @click="openStatusDialog(item)"
                :color="item.AccountDisabled ? 'success' : 'error'"
              >
                <v-icon>
                  {{ item.AccountDisabled ? 'mdi-account-check' : 'mdi-account-cancel' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ item.AccountDisabled ? 'Enable User' : 'Disable User' }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn 
                icon 
                v-on="on"
                color="info" 
                @click="openResetPasswordDialog(item)"
              >
                <v-icon>mdi-key</v-icon>
              </v-btn>
            </template>
            <span>Send Password Reset</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn 
                icon 
                v-on="on"
                color="error" 
                @click="openDeleteDialog(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Delete User</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-container>
  </div>
</template>

<script>
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { deleteUser, sendPasswordResetEmail } from "firebase/auth";

export default {
  name: "AdminUsersManagement",
  data() {
    return {
      statusDialog: false,
      deleteDialog: false,
      resetPasswordDialog: false,
      selectedUser: {
        id: '',
        name: '',
        email: '',
        mobile: '',
        AccountDisabled: false,
        uid: '' // Added uid for auth operations
      },
      statusFilter: "All",
      statusOptions: ["All", "Active", "Disabled"],
      adminUsers: [],
      search: "",
      loading: false,
      headers: [
        { text: "Name", value: "name", sortable: true },
        { text: "Email", value: "email", sortable: true },
        { text: "Phone", value: "mobile", sortable: true },
        { text: "Status", value: "status", sortable: true, align: 'center' },
        { text: "Actions", value: "actions", sortable: false, align: 'center', width: '200px' },
      ],
    };
  },
  methods: {
    openStatusDialog(user) {
      this.selectedUser = { ...user };
      this.statusDialog = true;
    },

    openDeleteDialog(user) {
      this.selectedUser = { ...user };
      this.deleteDialog = true;
    },

    openResetPasswordDialog(user) {
      this.selectedUser = { ...user };
      this.resetPasswordDialog = true;
    },

    async toggleUserStatus() {
      try {
        this.loading = true;
        const newStatus = !this.selectedUser.AccountDisabled;
        
        await setDoc(doc(db, "adminUsers", this.selectedUser.id), {
          AccountDisabled: newStatus,
          lastModified: new Date().toISOString()
        }, { merge: true });

        this.$store.dispatch('showSnackbar', {
          message: `User ${newStatus ? 'disabled' : 'enabled'} successfully`,
          color: 'success'
        });

        this.statusDialog = false;
        await this.getAdminUsers();
      } catch (error) {
        console.error("Error updating user status:", error);
        this.$store.dispatch('showSnackbar', {
          message: 'Failed to update user status',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async deleteUser() {
      try {
        this.loading = true;
        
        // Delete from Firestore
        await deleteDoc(doc(db, "adminUsers", this.selectedUser.id));
        
        // Delete from Authentication (if uid exists)
        if (this.selectedUser.uid) {
          await deleteUser(auth.currentUser, this.selectedUser.uid);
        }

        this.$store.dispatch('showSnackbar', {
          message: 'User deleted successfully',
          color: 'success'
        });

        this.deleteDialog = false;
        await this.getAdminUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        this.$store.dispatch('showSnackbar', {
          message: 'Failed to delete user',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async sendPasswordReset() {
      try {
        this.loading = true;
        await sendPasswordResetEmail(auth, this.selectedUser.email);
        
        this.$store.dispatch('showSnackbar', {
          message: 'Password reset email sent successfully',
          color: 'success'
        });

        this.resetPasswordDialog = false;
      } catch (error) {
        console.error("Error sending password reset email:", error);
        this.$store.dispatch('showSnackbar', {
          message: 'Failed to send password reset email',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async getAdminUsers() {
      this.loading = true;
      try {
        let q;
        if (this.statusFilter === "Active") {
          q = query(collection(db, "adminUsers"), where("AccountDisabled", "==", false));
        } else if (this.statusFilter === "Disabled") {
          q = query(collection(db, "adminUsers"), where("AccountDisabled", "==", true));
        } else {
          q = collection(db, "adminUsers");
        }

        const querySnapshot = await getDocs(q);
        this.adminUsers = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error("Error fetching admin users:", error);
        this.$store.dispatch('showSnackbar', {
          message: 'Failed to load users',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    goToPage(path) {
      this.$router.push({ path });
    },
  },
  created() {
    this.getAdminUsers();
  }
};
</script>

<style scoped>
.add-button {
  background-color: #f1c284;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-button:hover {
  background-color: #e0a858;
}

.button-container {
  margin-left: auto;
  padding: 10px;
}

.v-btn {
  margin: 0 4px;
}

.v-chip {
  font-weight: bold;
}

.data-table {
  margin-top: 20px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.text-error {
  color: #ff5252;
  font-weight: bold;
}
</style>